import { useState } from "react"

import { Button } from "@/components/ui"
import { setCookie } from "@/lib/utils/utils"
import { useTranslation } from "@/i18n"
import ZipmiLogo from "@/assets/images/zipmi_logo.svg"
import SpFlag from "@/assets/images/flags/sp.svg"
import EnFlag from "@/assets/images/flags/us.svg"

type LanguageOption = "en" | "es"

interface LanguageSelectionProps {
  onLanguageSelected: (language: LanguageOption) => void
}

export const LanguageSelection = ({
  onLanguageSelected,
}: LanguageSelectionProps) => {
  const { t } = useTranslation()
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageOption>("en")

  const handleContinue = () => {
    setCookie("language_preference", selectedLanguage)
    onLanguageSelected(selectedLanguage)
  }

  return (
    <div className="flex flex-col items-center justify-center gap-8 sm:gap-12 lg:gap-30 py-8 sm:py-12 lg:py-12 w-full">
      <div className="flex flex-1 flex-col gap-6 sm:gap-8 lg:gap-10 items-center justify-center w-full">
        {/* Logo */}
        <div className="inline-grid grid-cols-[max-content] grid-rows-[max-content] justify-items-start leading-0">
          <div className="col-1 h-auto mt-0.75 sm:mt-1 lg:mt-[4.27px] relative row-1 w-auto">
            <img
              alt="Join Zipmi"
              className="block max-w-none size-full"
              src={ZipmiLogo.src}
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-col gap-4 sm:gap-4 items-start w-full px-4 sm:px-6 lg:p-6">
          {/* Welcome Title */}
          <div className="flex flex-col items-start w-full">
            <h1 className="font-['DM_Sans',sans-serif] font-semibold leading-9 sm:leading-10 lg:leading-10.5 text-[#0038d0] text-[1.75rem] sm:text-3xl lg:text-[2rem] tracking-[-0.112px] sm:tracking-[-0.12px] lg:tracking-[-0.128px] w-full">
              {t("auth.languageSelection.welcomeToZipmi")}
            </h1>
          </div>

          {/* Description */}
          <div className="flex flex-col items-start w-full">
            <p className="font-['Inter',sans-serif] font-normal leading-4.5 text-sm text-[#171717] tracking-[-0.056px] w-full">
              {t("auth.languageSelection.selectLanguage")}
            </p>
          </div>

          {/* Language Options */}
          <div className="flex flex-col gap-4 sm:gap-4 items-start w-full mt-2">
            {/* English Option */}
            <button
              type="button"
              onClick={() => setSelectedLanguage("en")}
              className={`flex gap-2 sm:gap-2 items-center min-h-12.5 px-4 sm:px-4 py-3 sm:py-3 rounded-[4px] w-full border transition-all hover:opacity-90 ${
                selectedLanguage === "en"
                  ? "bg-blue-foreground border-[#4f9eff] border-[0.5px]"
                  : "bg-white border-[#e5e5e5] border-[0.5px] hover:border-[#d4d4d4]"
              }`}
            >
              {/* Radio Button */}
              <div className="relative shrink-0 size-6 sm:size-6">
                <div
                  className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 size-5 sm:size-5 ${
                    selectedLanguage === "en"
                      ? "bg-[#0038d0] rounded-full border-2 border-white"
                      : "bg-[#fafafa] rounded-full border-2 border-[#d4d4d4]"
                  }`}
                >
                  {selectedLanguage === "en" && (
                    <div className="absolute inset-[33.33%] bg-white rounded-full" />
                  )}
                </div>
              </div>

              {/* Flag */}
              <div className="relative shrink-0 size-6 sm:size-6">
                <div className="absolute h-3 sm:h-3.5 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-4.5 sm:w-5">
                  <img
                    alt="English flag"
                    className="absolute h-[101.47%] left-0 max-w-none top-[-0.74%] w-[108.33%] object-cover"
                    src={EnFlag.src}
                  />
                </div>
              </div>

              {/* Language Name */}
              <div className="flex flex-1 flex-col items-start justify-center min-h-0 min-w-0">
                <p className="font-['Inter',sans-serif] font-semibold leading-4.5 text-sm text-[#262626] tracking-[-0.028px]">
                  {t("auth.languageSelection.english")}
                </p>
              </div>
            </button>

            {/* Spanish Option */}
            <button
              type="button"
              onClick={() => setSelectedLanguage("es")}
              className={`flex gap-2 sm:gap-2 items-center min-h-12.5 px-4 sm:px-4 py-3 sm:py-3 rounded-[4px] w-full border transition-all hover:opacity-90 ${
                selectedLanguage === "es"
                  ? "bg-blue-foreground border-[#4f9eff] border-[0.5px]"
                  : "bg-white border-[#e5e5e5] border-[0.5px] hover:border-[#d4d4d4]"
              }`}
            >
              {/* Radio Button */}
              <div className="relative shrink-0 size-6 sm:size-6">
                <div
                  className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 size-5 sm:size-5 ${
                    selectedLanguage === "es"
                      ? "bg-[#0038d0] rounded-full border-2 border-white"
                      : "bg-[#fafafa] rounded-full border-2 border-[#d4d4d4]"
                  }`}
                >
                  {selectedLanguage === "es" && (
                    <div className="absolute inset-[33.33%] bg-white rounded-full" />
                  )}
                </div>
              </div>

              {/* Flag */}
              <div className="overflow-clip relative shrink-0 size-6 sm:size-6">
                <div className="absolute inset-[20.83%_8.33%]">
                  <img
                    alt="Spanish flag"
                    className="block max-w-none size-full object-cover"
                    src={SpFlag.src}
                  />
                </div>
              </div>

              {/* Language Name */}
              <div className="flex flex-1 flex-col items-start justify-center min-h-0 min-w-0">
                <p className="font-['Inter',sans-serif] font-semibold leading-4.5 text-sm text-[#262626] tracking-[-0.028px]">
                  {t("auth.languageSelection.spanish")}
                </p>
              </div>
            </button>
          </div>
        </div>

        {/* Continue Button */}
        <Button
          onClick={handleContinue}
          className="bg-[#0038d0] h-11 min-h-11 px-4 sm:px-4 py-2.5 rounded-[8px] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.09)] w-full hover:bg-[#0038d0]/90 transition-colors"
        >
          <span className="font-['Inter',sans-serif] font-semibold leading-5.5 text-base text-[#f5f5f5] text-center whitespace-nowrap">
            {t("auth.languageSelection.continue")}
          </span>
        </Button>
      </div>
    </div>
  )
}
